// Draft Service - Real-time draft management with GraphQL subscriptions
import { generateClient } from 'aws-amplify/api';

// Lazy-load the client to ensure Amplify is configured first
let client = null;
let clientError = null;

const getClient = () => {
  if (clientError) {
    throw clientError;
  }
  
  if (!client) {
    try {
      client = generateClient();
      
      if (!client) {
        throw new Error('GraphQL client is null. Ensure Amplify is configured.');
      }
    } catch (error) {
      console.error('❌ Failed to generate GraphQL client:', error);
      clientError = error;
      throw error;
    }
  }
  
  return client;
};

// GraphQL queries and mutations
const createDraftMutation = /* GraphQL */ `
  mutation CreateDraft($input: CreateDraftInput!) {
    createDraft(input: $input) {
      leagueId
      status
      settings
      currentPickNumber
      currentPickTeamId
      draftOrder
      picks
      teams
      startedAt
      updatedAt
    }
  }
`;

const updateDraftMutation = /* GraphQL */ `
  mutation UpdateDraft($input: UpdateDraftInput!) {
    updateDraft(input: $input) {
      leagueId
      status
      settings
      currentPickNumber
      currentPickTeamId
      draftOrder
      teams
      picks
      startedAt
      completedAt
      pausedAt
      pickStartTime
      updatedAt
    }
  }
`;

const getDraftQuery = /* GraphQL */ `
  query GetDraft($leagueId: String!) {
    getDraft(leagueId: $leagueId) {
      leagueId
      status
      settings
      currentPickNumber
      currentPickTeamId
      draftOrder
      teams
      picks
      startedAt
      completedAt
      pausedAt
      pickStartTime
      updatedAt
    }
  }
`;

const createDraftPickMutation = /* GraphQL */ `
  mutation CreateDraftPick($input: CreateDraftPickInput!) {
    createDraftPick(input: $input) {
      leagueId
      pickNumber
      round
      pickInRound
      teamId
      teamName
      playerId
      playerName
      playerPosition
      playerTeam
      timestamp
      pickType
    }
  }
`;

const onDraftUpdateSubscription = /* GraphQL */ `
  subscription OnDraftUpdate($leagueId: String!) {
    onUpdateDraft(filter: { leagueId: { eq: $leagueId } }) {
      leagueId
      status
      settings
      currentPickNumber
      currentPickTeamId
      draftOrder
      teams
      picks
      startedAt
      completedAt
      pausedAt
      pickStartTime
      updatedAt
    }
  }
`;

const onDraftPickCreatedSubscription = /* GraphQL */ `
  subscription OnDraftPickCreated($leagueId: String!) {
    onCreateDraftPick(filter: { leagueId: { eq: $leagueId } }) {
      leagueId
      pickNumber
      round
      pickInRound
      teamId
      teamName
      playerId
      playerName
      playerPosition
      playerTeam
      timestamp
      pickType
    }
  }
`;

// Initialize a new draft
export const createDraft = async (draftData) => {
  try {
    console.log('📝 Creating draft with data:', draftData);
    
    const response = await getClient().graphql({
      query: createDraftMutation,
      variables: {
        input: {
          ...draftData,
          settings: JSON.stringify(draftData.settings),
          draftOrder: JSON.stringify(draftData.draftOrder),
          teams: JSON.stringify(draftData.teams || []),
          picks: JSON.stringify(draftData.picks || [])
        }
      },
      authMode: 'userPool'
    });

    // Check for GraphQL errors
    if (response.errors && response.errors.length > 0) {
      const errorMessage = response.errors.map(e => e.message).join(', ');
      console.error('❌ GraphQL errors creating draft:', response.errors);
      throw new Error(`Failed to create draft: ${errorMessage}`);
    }

    if (!response.data || !response.data.createDraft) {
      console.error('❌ No data returned from createDraft:', response);
      throw new Error('Failed to create draft: No data returned from server');
    }

    const draft = response.data.createDraft;
    console.log('✅ Draft created successfully:', draft);
    
    return {
      ...draft,
      settings: JSON.parse(draft.settings),
      draftOrder: JSON.parse(draft.draftOrder),
      teams: JSON.parse(draft.teams || '[]'),
      picks: JSON.parse(draft.picks || '[]')
    };
  } catch (error) {
    console.error('❌ Error creating draft:', error);
    
    // Handle GraphQL errors
    if (error.errors && error.errors.length > 0) {
      const errorMessages = error.errors.map(e => e.message).join(', ');
      throw new Error(`GraphQL Error: ${errorMessages}`);
    }
    
    throw error;
  }
};

// Update draft state
export const updateDraft = async (draftData) => {
  try {
    const response = await getClient().graphql({
      query: updateDraftMutation,
      variables: {
        input: {
          leagueId: draftData.leagueId,
          status: draftData.status,
          settings: JSON.stringify(draftData.settings),
          currentPickNumber: draftData.currentPickNumber,
          currentPickTeamId: draftData.currentPickTeamId,
          draftOrder: JSON.stringify(draftData.draftOrder),
          teams: JSON.stringify(draftData.teams || []),
          picks: JSON.stringify(draftData.picks || []),
          startedAt: draftData.startedAt,
          completedAt: draftData.completedAt,
          pausedAt: draftData.pausedAt,
          pickStartTime: draftData.pickStartTime
        }
      },
      authMode: 'userPool'
    });

    if (response.errors && response.errors.length > 0) {
      const msg = response.errors.map(e => e.message).join(', ');
      throw new Error(`Failed to update draft: ${msg}`);
    }
    if (!response.data?.updateDraft) {
      throw new Error('Failed to update draft: no data returned');
    }

    const draft = response.data.updateDraft;
    return {
      ...draft,
      settings: JSON.parse(draft.settings),
      draftOrder: JSON.parse(draft.draftOrder),
      teams: JSON.parse(draft.teams || '[]'),
      picks: JSON.parse(draft.picks || '[]')
    };
  } catch (error) {
    console.error('Error updating draft:', error);
    throw error;
  }
};

// Get current draft state
export const getDraft = async (leagueId) => {
  try {
    const response = await getClient().graphql({
      query: getDraftQuery,
      variables: { leagueId },
      authMode: 'userPool'
    });

    const draft = response.data.getDraft;
    if (!draft) return null;

    return {
      ...draft,
      settings: JSON.parse(draft.settings),
      draftOrder: JSON.parse(draft.draftOrder),
      teams: JSON.parse(draft.teams || '[]'),
      picks: JSON.parse(draft.picks || '[]')
    };
  } catch (error) {
    console.error('Error fetching draft:', error);
    return null;
  }
};

// Make a draft pick (creates event and updates draft)
export const makeDraftPick = async (leagueId, pickData, updatedDraft) => {
  try {
    // Create the pick event
    const pickResponse = await getClient().graphql({
      query: createDraftPickMutation,
      variables: {
        input: {
          leagueId,
          pickNumber: pickData.pickNumber,
          round: pickData.round,
          pickInRound: pickData.pickInRound,
          teamId: pickData.teamId,
          teamName: pickData.teamName,
          playerId: pickData.playerId,
          playerName: pickData.playerName,
          playerPosition: pickData.playerPosition,
          playerTeam: pickData.playerTeam,
          timestamp: pickData.timestamp,
          pickType: pickData.pickType || 'manual'
        }
      },
      authMode: 'userPool'
    });

    if (pickResponse.errors?.length > 0) {
      const msg = pickResponse.errors.map(e => e.message).join(', ');
      throw new Error(`Failed to create pick: ${msg}`);
    }
    if (!pickResponse.data?.createDraftPick) {
      throw new Error('Failed to create pick: no data returned');
    }

    // Update the draft state
    const draftResponse = await updateDraft(updatedDraft);

    return {
      success: true,
      pick: pickResponse.data.createDraftPick,
      draft: draftResponse
    };
  } catch (error) {
    console.error('Error making draft pick:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Subscribe to draft updates (real-time)
export const subscribeToDraftUpdates = (leagueId, onUpdate) => {
  try {
    const subscription = getClient().graphql({
      query: onDraftUpdateSubscription,
      variables: { leagueId },
      authMode: 'userPool'
    }).subscribe({
      next: ({ data }) => {
        const draft = data.onUpdateDraft;
        if (draft) {
          const parsedDraft = {
            ...draft,
            settings: JSON.parse(draft.settings),
            draftOrder: JSON.parse(draft.draftOrder),
            teams: JSON.parse(draft.teams || '[]'),
            picks: JSON.parse(draft.picks || '[]')
          };
          onUpdate(parsedDraft);
        }
      },
      error: (error) => {
        console.error('Draft subscription error:', error);
      }
    });

    return subscription;
  } catch (error) {
    console.error('Error setting up draft subscription:', error);
    return null;
  }
};

// Subscribe to new picks (real-time)
export const subscribeToDraftPicks = (leagueId, onNewPick) => {
  try {
    const subscription = getClient().graphql({
      query: onDraftPickCreatedSubscription,
      variables: { leagueId },
      authMode: 'userPool'
    }).subscribe({
      next: ({ data }) => {
        const pick = data.onCreateDraftPick;
        if (pick) {
          onNewPick(pick);
        }
      },
      error: (error) => {
        console.error('Pick subscription error:', error);
      }
    });

    return subscription;
  } catch (error) {
    console.error('Error setting up pick subscription:', error);
    return null;
  }
};

// Start the draft
export const startDraft = async (leagueId) => {
  try {
    const draft = await getDraft(leagueId);
    if (!draft) {
      throw new Error('Draft not found');
    }

    const updatedDraft = {
      ...draft,
      status: 'in_progress',
      startedAt: new Date().toISOString(),
      pickStartTime: new Date().toISOString()
    };

    return await updateDraft(updatedDraft);
  } catch (error) {
    console.error('Error starting draft:', error);
    throw error;
  }
};

// Pause the draft
export const pauseDraft = async (leagueId) => {
  try {
    const draft = await getDraft(leagueId);
    if (!draft) {
      throw new Error('Draft not found');
    }

    const updatedDraft = {
      ...draft,
      status: 'paused',
      pausedAt: new Date().toISOString()
    };

    return await updateDraft(updatedDraft);
  } catch (error) {
    console.error('Error pausing draft:', error);
    throw error;
  }
};

// Resume the draft
export const resumeDraft = async (leagueId) => {
  try {
    const draft = await getDraft(leagueId);
    if (!draft) {
      throw new Error('Draft not found');
    }

    const updatedDraft = {
      ...draft,
      status: 'in_progress',
      pausedAt: null,
      pickStartTime: new Date().toISOString()
    };

    return await updateDraft(updatedDraft);
  } catch (error) {
    console.error('Error resuming draft:', error);
    throw error;
  }
};

// Toggle auto-pick for a team (add/remove from autoDraftTeamIds)
export const toggleAutoPick = async (leagueId, draft, teamId, enabled) => {
  try {
    const autoIds = [...(draft.settings?.autoDraftTeamIds || [])];
    const idx = autoIds.indexOf(teamId);
    if (enabled && idx === -1) {
      autoIds.push(teamId);
    } else if (!enabled && idx !== -1) {
      autoIds.splice(idx, 1);
    } else {
      return draft; // no change
    }
    const updatedDraft = {
      ...draft,
      settings: {
        ...draft.settings,
        autoDraftTeamIds: autoIds
      }
    };
    return await updateDraft(updatedDraft);
  } catch (error) {
    console.error('Error toggling auto-pick:', error);
    throw error;
  }
};

export default {
  createDraft,
  updateDraft,
  getDraft,
  makeDraftPick,
  toggleAutoPick,
  subscribeToDraftUpdates,
  subscribeToDraftPicks,
  startDraft,
  pauseDraft,
  resumeDraft
};


